import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";

import { TEAM_MEETINGS_DEFAULT_PARTICIPANT_LIMIT } from "@performyard/models";
import { toast } from "@performyard/py-components";
import type { ExtendedWorkspace } from "@performyard/service-contracts";

import type { EmployeeValue } from "../../../../components/Inputs/SelectEmployee";
import { createFetchEmployeesForPicker } from "../../../../components/Inputs/SelectEmployee/shared";
import WorkspaceFormModal from "./WorkspaceFormModal";

import { useCurrentUser, useUpdateMeetingWorkspace } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

type UpdateWorkspaceFormProps = {
	isOpen: boolean;
	handleClose: () => void;
	workspace: ExtendedWorkspace | null;
};

const UpdateWorkspaceForm = ({ isOpen, handleClose, workspace }: UpdateWorkspaceFormProps) => {
	const queryClient = useQueryClient();
	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload.user_id ?? "";

	// Feature flags
	const { value: isTeamMeetingsEnabled } = useFeatureFlagQuery("ff-is-team-meetings-enabled", false);
	const { value: participantLimit } = useFeatureFlagQuery(
		"ff-config-team-meetings-participant-limit",
		TEAM_MEETINGS_DEFAULT_PARTICIPANT_LIMIT
	);

	// Update workspace mutation
	const { mutate: updateWorkspace, isPending } = useUpdateMeetingWorkspace({
		onSuccess: () => {
			toast.success("Meeting Workspace Updated");
			handleClose();
		},
	});

	// Build default participants from workspace data (excluding current user / creator).
	const defaultParticipants: EmployeeValue[] = useMemo(() => {
		if (!workspace?.participants) return [];
		return workspace.participants
			.filter((p) => p.userId !== currentUserId)
			.map((p) => ({
				id: p.userId,
				name: p.name || "",
			}));
	}, [workspace?.participants, currentUserId]);

	// Fetch employees excluding current user
	const fetchEmployees = useCallback(
		async (searchTerm: string) => {
			const defaultFetch = createFetchEmployeesForPicker(queryClient);
			const employees = await defaultFetch(searchTerm);
			return employees.filter((emp) => emp.id !== currentUserId);
		},
		[queryClient, currentUserId]
	);

	const handleSubmit = (values: { name: string; participants: EmployeeValue[] }) => {
		if (!currentUserId || !workspace) return;

		// Only include participants when Team Meetings FF is on;
		// when off, the picker is disabled and we preserve the original participants
		const participants = isTeamMeetingsEnabled
			? [
					{ role: "editor" as const, userId: currentUserId },
					...values.participants.map((p) => ({ role: "editor" as const, userId: p.id })),
				]
			: undefined;

		updateWorkspace({
			workspaceId: workspace._id,
			data: {
				name: values.name,
				...(participants && { participants }),
			},
		});
	};

	// When Team Meetings FF is off, participants are not editable
	const isParticipantEditable = isTeamMeetingsEnabled;

	return (
		<WorkspaceFormModal
			isOpen={isOpen}
			onClose={handleClose}
			onSubmit={handleSubmit}
			isPending={isPending}
			title="Edit Meeting Workspace"
			submitLabel="Save Changes"
			defaultName={workspace?.name ?? ""}
			defaultParticipants={defaultParticipants}
			isTeamMeetingsEnabled={isTeamMeetingsEnabled}
			participantLimit={participantLimit}
			isParticipantEditable={isParticipantEditable}
			fetchEmployees={fetchEmployees}
		/>
	);
};

export default UpdateWorkspaceForm;
